import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type PropsWithChildren,
} from 'react';
import SendbirdChat, { type SendbirdChatWith } from '@sendbird/chat';
import { GroupChannelModule } from '@sendbird/chat/groupChannel';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import { sendBirdColorSet } from './colorSet';
import { useFetchSendBirdAppId } from '../../api/hooks/useFetchSendBirdAppId';
import { Error as ErrorPage } from '../../components/admin/error';
import { Loading } from '../../components/admin/loading';
import { useGoToChannel } from './useGoToChannel';
import UserProfile from './UserProfile';
import { useCurrentUser } from '../../auth/currentUserContext';
import { SendbirdConnectionManager } from './SendbirdConnectionManager';
import { useHasPermission } from '../../auth/useHasPermission';
import { useResolvedThemeMode } from '../../layout/useResolvedThemeMode';

type SendbirdContextValue = {
  sb: SendbirdChatWith<GroupChannelModule[]> | undefined;
};

const SendbirdContext = createContext<SendbirdContextValue | undefined>(
  undefined
);

export const SendbirdProviderWithSDK: FC<PropsWithChildren> = ({
  children,
}) => {
  const user = useCurrentUser();
  const hasChatAccess = useHasPermission('CHAT_ACCESS');
  const themeMode = useResolvedThemeMode();
  const {
    data: sendBirdApp,
    isPending,
    isError,
    error,
    refetch,
  } = useFetchSendBirdAppId({ enabled: hasChatAccess });

  const sendbirdInstance = useMemo(() => {
    if (!sendBirdApp?.appId) {
      return undefined;
    }

    return SendbirdChat.init({
      appId: sendBirdApp.appId,
      modules: [new GroupChannelModule()],
    });
  }, [sendBirdApp]);

  const contextValue = useMemo(
    () => ({ sb: sendbirdInstance }),
    [sendbirdInstance]
  );

  const goToChannel = useGoToChannel();

  if (!hasChatAccess) {
    // Chat is disabled for this user: skip Sendbird entirely (no app-id fetch,
    // no SDK init, no UIKit connection), but still provide the context so
    // consumers such as the unread-count hook don't throw.
    return (
      <SendbirdContext.Provider value={contextValue}>
        {children}
      </SendbirdContext.Provider>
    );
  }

  if (isPending) {
    return <Loading />;
  }

  if (isError) {
    return (
      <ErrorPage error={error} resetErrorBoundary={() => void refetch()} />
    );
  }

  return (
    <SendbirdContext.Provider value={contextValue}>
      <SendbirdProvider
        appId={sendBirdApp.appId}
        userId={user.id.toString()}
        theme={themeMode}
        colorSet={sendBirdColorSet}
        renderUserProfile={(props) => <UserProfile {...props} />}
        onStartDirectMessage={(channel) => void goToChannel(channel.url)}
      >
        <SendbirdConnectionManager>{children}</SendbirdConnectionManager>
      </SendbirdProvider>
    </SendbirdContext.Provider>
  );
};

export const useSendbirdContext = (): SendbirdContextValue => {
  const context = useContext(SendbirdContext);
  if (!context) {
    throw new Error(
      'useSendbirdContext must be used within a SendbirdProviderWithSDK.'
    );
  }
  return context;
};
